import type { TestType } from '@risksmart-app/domain/src/types/consts/test-type';
import type {
  UpdateTestResultInput,
  UpdateTestResultMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateTestResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

type UpdateTestResultHookInput = Omit<
  UpdateTestResultInput,
  'TestType' | 'Submitter' | 'TestDate'
> & {
  TestType?: TestType | null;
  OriginalTimestamp: string;
  // COI-645: Submitter and TestDate back NOT NULL columns, so the tRPC
  // update input no longer admits null — omitted preserves the stored
  // value. Both call sites always send real values (the form requires
  // them); this narrows the GraphQL-generated `InputMaybe` looseness to
  // the actual contract.
  Submitter?: string;
  TestDate?: string;
};

export const useUpdateTestResult = createMutationHook({
  name: 'updateTestResult',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.testResult.update.mutationOptions({ onSuccess }),
  mapInput: (input: UpdateTestResultHookInput) => input,
  invalidate: (trpc) => [
    trpc.frontend.testResult.testResultById.queryKey(),
    trpc.frontend.testResult.testResultsByControlId.queryKey(),
    trpc.frontend.testResult.latestTestResultsByControlId.queryKey(),
    trpc.frontend.testResult.testResults.queryKey(),
    // COI-889: the control register's Design / Performance / Overall
    // effectiveness columns read test results; `controlById` does not.
    trpc.frontend.control.pathKey(),
    // COI-471 parity: Apollo twin also evicts `risk_score` (test results
    // roll up into control effectiveness / risk scores).
    trpc.frontend.risk.pathKey(),
  ],
  mapResponse: (result: { Id: string }): UpdateTestResultMutation => ({
    updateTestResultApi: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: UpdateTestResultDocument,
  graphqlVariables: (input: UpdateTestResultHookInput) => ({ object: input }),
  evictFields: ['control', 'risk_score'],
});
